/**
 * 電子發票前端服務（平台先用·/api/ee/einvoice/*）。
 * axios 攔截器自動附加 Authorization + X-Txn-Token，服務層不手動傳 token。
 * 機密（憑證 / 密碼 / 傳送帳號）只送不收——回應一律遮罩（has_cert / has_transmit_account）。
 */
import axios from '../api/axios';

const BASE = '/api/ee/einvoice';

export type EInvoiceEnv = 'test' | 'prod';

export interface EInvoiceConfig {
  enabled: boolean;
  environment: EInvoiceEnv;
  seller_tax_id?: string | null;
  seller_name?: string | null;
  seller_address?: string | null;
  routing_code?: string | null;
  has_cert: boolean;
  cert_subject?: string | null;
  cert_expire_at?: string | null;
  has_transmit_account: boolean;
  complete: boolean;
}

export interface EInvoiceConfigUpdate {
  enabled?: boolean;
  environment?: EInvoiceEnv;
  seller_tax_id?: string | null;
  seller_name?: string | null;
  seller_address?: string | null;
  routing_code?: string | null;
  transmit_account?: string | null; // 機密·只送
}

export interface Track {
  id: number;
  period: string;
  track: string;
  environment: EInvoiceEnv;
  begin_no: number;
  end_no: number;
  next_no: number;
  is_active: boolean;
}

export interface TrackCreate {
  period: string;
  track: string;
  environment: EInvoiceEnv;
  begin_no: number;
  end_no: number;
}

export type InvoiceStatus = 'issued' | 'void';
export type UploadStatus = 'pending' | 'uploaded' | 'failed';

export interface IssuedInvoice {
  id: number;
  source_module: string;
  source_id: number;
  invoice_no: string;
  period: string;
  environment: EInvoiceEnv;
  invoice_date: string;
  invoice_time: string;
  buyer_tax_id?: string | null;
  buyer_title?: string | null;
  seller_tax_id: string;
  sales_amount: number;
  tax_amount: number;
  total_amount: number;
  status: InvoiceStatus;
  upload_status: UploadStatus;
  upload_error?: string | null;
  void_reason?: string | null;
}

export interface EInvoiceUsageItem {
  name: string;
  amount: number;          // 未稅定價（折抵為負）
  kind: string;
  promo_incl?: number | null;  // 含稅優惠價（帶入行才有·建議優惠價用）
}

export interface EInvoiceUsage {
  enabled: boolean;
  free: boolean;
  mailbox_count: number;
  total_amount: number;
  items: EInvoiceUsageItem[];
}

// ── 設定 ──────────────────────────────────────────────
export const getConfig = async (): Promise<EInvoiceConfig> =>
  (await axios.get(`${BASE}/config`)).data;

export const saveConfig = async (data: EInvoiceConfigUpdate): Promise<EInvoiceConfig> =>
  (await axios.put(`${BASE}/config`, data)).data;

export const uploadCertificate = async (
  file: File, password: string,
): Promise<EInvoiceConfig> => {
  // S3：主體 / 到期日由後端從憑證自動解析，前端不再輸入。
  const fd = new FormData();
  fd.append('file', file);
  fd.append('password', password);
  // 覆蓋預設 json content-type，讓瀏覽器自帶 multipart boundary
  return (await axios.post(`${BASE}/config/certificate`, fd,
    { headers: { 'Content-Type': 'multipart/form-data' } })).data;
};

// ── 字軌 ──────────────────────────────────────────────
export const listTracks = async (environment?: EInvoiceEnv): Promise<Track[]> =>
  (await axios.get(`${BASE}/tracks`, { params: environment ? { environment } : {} })).data;

export const addTrack = async (data: TrackCreate): Promise<Track> =>
  (await axios.post(`${BASE}/tracks`, data)).data;

// ── 發票維護 ──────────────────────────────────────────
export const listInvoices = async (params?: {
  year?: number; month?: number; status?: InvoiceStatus; environment?: EInvoiceEnv;
}): Promise<IssuedInvoice[]> => (await axios.get(`${BASE}/invoices`, { params })).data;

export const voidInvoice = async (id: number, reason: string): Promise<IssuedInvoice> =>
  (await axios.post(`${BASE}/invoices/${id}/void`, { reason })).data;

export const resendInvoice = async (id: number): Promise<IssuedInvoice> =>
  (await axios.post(`${BASE}/invoices/${id}/resend`)).data;

// ── 帳單帶入電子發票模組服務費（§9）─────────────────────
export const computeUsage = async (
  tenantOrgId: number, year: number, month: number,
): Promise<EInvoiceUsage> =>
  (await axios.get(`${BASE}/usage-line`,
    { params: { tenant_org_id: tenantOrgId, year, month } })).data;
